import { z } from 'zod';
import { haversineDistanceMeters } from '@pacevault/engine/geodesy.ts';
import type { SessionRecord } from '@pacevault/engine/types.ts';
import type { WindSample } from '@pacevault/engine/windExposure.ts';
import { FailedResponseError } from '@/lib/api/fetchResponse.ts';
import { MS_PER_HOUR, MS_PER_SECOND } from '@pacevault/engine/time.ts';
import type { SessionWeather, WeatherSnapshot } from '@/types.ts';

export type WeatherCondition =
  | 'clear'
  | 'partly-cloudy'
  | 'cloudy'
  | 'fog'
  | 'drizzle'
  | 'rain'
  | 'snow'
  | 'thunderstorm';

export const wmoToCondition = (code: number): WeatherCondition => {
  if (code === 0) return 'clear';
  if (code >= 1 && code <= 2) return 'partly-cloudy';
  if (code === 3) return 'cloudy';
  if (code === 45 || code === 48) return 'fog';
  if (code >= 51 && code <= 57) return 'drizzle';
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return 'rain';
  if ((code >= 71 && code <= 77) || (code >= 85 && code <= 86)) return 'snow';
  if (code >= 95 && code <= 99) return 'thunderstorm';
  return 'cloudy';
};

export type WeatherDescription =
  | WeatherCondition
  | 'drizzle-light'
  | 'drizzle-moderate'
  | 'drizzle-dense'
  | 'freezing-drizzle-light'
  | 'freezing-drizzle-dense'
  | 'rain-light'
  | 'rain-moderate'
  | 'rain-heavy'
  | 'freezing-rain-light'
  | 'freezing-rain-heavy'
  | 'snow-light'
  | 'snow-moderate'
  | 'snow-heavy'
  | 'snow-grains'
  | 'rain-showers-light'
  | 'rain-showers-moderate'
  | 'rain-showers-heavy'
  | 'snow-showers-light'
  | 'snow-showers-heavy'
  | 'thunderstorm-hail'
  | 'thunderstorm-heavy'
  | 'thunderstorm-hail-heavy';

const DESCRIPTION_BY_CODE: Partial<Record<number, WeatherDescription>> = {
  51: 'drizzle-light',
  53: 'drizzle-moderate',
  55: 'drizzle-dense',
  56: 'freezing-drizzle-light',
  57: 'freezing-drizzle-dense',
  61: 'rain-light',
  63: 'rain-moderate',
  65: 'rain-heavy',
  66: 'freezing-rain-light',
  67: 'freezing-rain-heavy',
  71: 'snow-light',
  73: 'snow-moderate',
  75: 'snow-heavy',
  77: 'snow-grains',
  80: 'rain-showers-light',
  81: 'rain-showers-moderate',
  82: 'rain-showers-heavy',
  85: 'snow-showers-light',
  86: 'snow-showers-heavy',
  95: 'thunderstorm',
  96: 'thunderstorm-hail',
  97: 'thunderstorm-heavy',
  99: 'thunderstorm-hail-heavy',
};

export const wmoToDescription = (code: number): WeatherDescription => {
  const description = DESCRIPTION_BY_CODE[code];
  if (description !== undefined) return description;
  return wmoToCondition(code);
};

type PrecipitationIntensity = 'none' | 'light' | 'moderate' | 'heavy';

const INTENSITY_BY_CODE: Partial<Record<number, PrecipitationIntensity>> = {
  51: 'light',
  56: 'light',
  61: 'light',
  66: 'light',
  71: 'light',
  77: 'light',
  80: 'light',
  85: 'light',
  53: 'moderate',
  63: 'moderate',
  73: 'moderate',
  81: 'moderate',
  95: 'moderate',
  96: 'moderate',
  55: 'heavy',
  57: 'heavy',
  65: 'heavy',
  67: 'heavy',
  75: 'heavy',
  82: 'heavy',
  86: 'heavy',
  97: 'heavy',
  99: 'heavy',
};

const DRY_CONDITIONS: ReadonlySet<WeatherCondition> = new Set([
  'clear',
  'partly-cloudy',
  'cloudy',
  'fog',
]);

export const wmoToPrecipitation = (code: number): PrecipitationIntensity | undefined => {
  const intensity = INTENSITY_BY_CODE[code];
  if (intensity !== undefined) return intensity;
  if (DRY_CONDITIONS.has(wmoToCondition(code))) return 'none';
  return undefined;
};

export type PrecipitationType = 'rain' | 'freezing' | 'snow' | 'thunderstorm';

export type PrecipitationDots = 0 | 1 | 2 | 3;

export interface PrecipitationMark {
  dots: PrecipitationDots | undefined;
  type: PrecipitationType | undefined;
}

const DOTS_BY_INTENSITY: Record<PrecipitationIntensity, PrecipitationDots> = {
  none: 0,
  light: 1,
  moderate: 2,
  heavy: 3,
};

const FREEZING_CODES: ReadonlySet<number> = new Set([56, 57, 66, 67]);

const PRECIPITATION_BY_CONDITION: Partial<Record<WeatherCondition, PrecipitationType>> = {
  drizzle: 'rain',
  rain: 'rain',
  snow: 'snow',
  thunderstorm: 'thunderstorm',
};

const precipitationType = (code: number): PrecipitationType | undefined => {
  if (FREEZING_CODES.has(code)) return 'freezing';
  return PRECIPITATION_BY_CONDITION[wmoToCondition(code)];
};

export const wmoToPrecipitationMark = (code: number): PrecipitationMark => {
  const intensity = wmoToPrecipitation(code);
  let dots: PrecipitationDots | undefined = undefined;
  if (intensity !== undefined) dots = DOTS_BY_INTENSITY[intensity];
  return { dots, type: precipitationType(code) };
};

interface Waypoint {
  time: number;
  lat: number;
  lng: number;
}

export const computeHourlyWaypoints = ({
  sessionDateMs,
  durationSec,
  records,
}: {
  sessionDateMs: number;
  durationSec: number;
  records: SessionRecord[];
}): Waypoint[] => {
  const gpsRecords = records.filter((r) => r.lat !== undefined && r.lng !== undefined);
  if (gpsRecords.length === 0) return [];

  const sessionEndMs = sessionDateMs + durationSec * MS_PER_SECOND;

  const firstHour = Math.floor(sessionDateMs / MS_PER_HOUR) * MS_PER_HOUR;
  const hours: number[] = [];
  let h = firstHour;
  while (h <= sessionEndMs) {
    if (h >= sessionDateMs - MS_PER_HOUR) {
      hours.push(h);
    }
    h += MS_PER_HOUR;
  }

  if (hours.length === 0) {
    hours.push(firstHour);
  }

  const waypoints: Waypoint[] = [];
  for (const hourTimestamp of hours) {
    const elapsedTarget = (hourTimestamp - sessionDateMs) / MS_PER_SECOND;
    let closest = gpsRecords[0];
    if (closest === undefined) continue;
    let closestDiff = Math.abs(closest.timestamp - elapsedTarget);

    for (const r of gpsRecords) {
      const diff = Math.abs(r.timestamp - elapsedTarget);
      if (diff < closestDiff) {
        closest = r;
        closestDiff = diff;
      }
    }

    if (closest.lat !== undefined && closest.lng !== undefined) {
      waypoints.push({
        time: hourTimestamp,
        lat: closest.lat,
        lng: closest.lng,
      });
    }
  }

  return waypoints;
};

interface WaypointCluster {
  lat: number;
  lng: number;
  waypointIndices: number[];
}

export const deduplicateWaypoints = ({
  waypoints,
  thresholdKm,
}: {
  waypoints: Waypoint[];
  thresholdKm: number;
}): WaypointCluster[] => {
  const clusters: WaypointCluster[] = [];

  for (let i = 0; i < waypoints.length; i++) {
    const wp = waypoints[i];
    if (!wp) continue;

    let merged = false;
    for (const cluster of clusters) {
      if (haversineDistanceMeters({ from: wp, to: cluster }) < thresholdKm * 1000) {
        cluster.waypointIndices.push(i);
        merged = true;
        break;
      }
    }

    if (!merged) {
      clusters.push({ lat: wp.lat, lng: wp.lng, waypointIndices: [i] });
    }
  }

  return clusters;
};

// The API is queried with timezone=UTC, so date params must be UTC days —
// browser-local dates would shift the requested window near midnight.
const toUtcDateStr = (ms: number): string => {
  const d = new Date(ms);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

export const buildWeatherUrl = ({
  lat,
  lng,
  startDate,
  endDate,
}: {
  lat: number;
  lng: number;
  startDate: string;
  endDate: string;
}): string => {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lng),
    start_date: startDate,
    end_date: endDate,
    hourly:
      'temperature_2m,apparent_temperature,relative_humidity_2m,wind_speed_10m,wind_gusts_10m,wind_direction_10m,weather_code',
    timezone: 'UTC',
    timeformat: 'unixtime',
  });
  return `https://archive-api.open-meteo.com/v1/archive?${params.toString()}`;
};

const openMeteoHourlySchema = z.object({
  // unix epoch seconds (timeformat=unixtime)
  time: z.array(z.number()),
  temperature_2m: z.array(z.number().nullable()),
  apparent_temperature: z.array(z.number().nullable()),
  relative_humidity_2m: z.array(z.number().nullable()),
  wind_speed_10m: z.array(z.number().nullable()),
  wind_gusts_10m: z.array(z.number().nullable()),
  wind_direction_10m: z.array(z.number().nullable()),
  weather_code: z.array(z.number().nullable()),
});

const openMeteoResponseSchema = z.object({
  hourly: openMeteoHourlySchema,
});

type OpenMeteoResponse = z.infer<typeof openMeteoResponseSchema>;

const fetchClusterWeather = async ({
  lat,
  lng,
  startDate,
  endDate,
}: {
  lat: number;
  lng: number;
  startDate: string;
  endDate: string;
}): Promise<OpenMeteoResponse | undefined> => {
  const url = buildWeatherUrl({ lat, lng, startDate, endDate });
  const response = await fetch(url);
  if (!response.ok) {
    throw new FailedResponseError({ operation: 'Open-Meteo GET archive', response });
  }
  const json: unknown = await response.json();
  const result = openMeteoResponseSchema.safeParse(json);
  if (!result.success) return undefined;
  return result.data;
};

const pickHourIndex = ({
  targetMs,
  timesEpochSec,
}: {
  targetMs: number;
  timesEpochSec: number[];
}): number => {
  let bestIdx = 0;
  let bestDiff = Number.POSITIVE_INFINITY;

  for (let i = 0; i < timesEpochSec.length; i++) {
    const timeSec = timesEpochSec[i];
    if (timeSec === undefined) continue;
    const diff = Math.abs(timeSec * MS_PER_SECOND - targetMs);
    if (diff < bestDiff) {
      bestIdx = i;
      bestDiff = diff;
    }
  }

  return bestIdx;
};

const snapshotAt = ({
  waypoint,
  data,
}: {
  waypoint: Waypoint;
  data: OpenMeteoResponse;
}): WeatherSnapshot | undefined => {
  const hourIdx = pickHourIndex({ targetMs: waypoint.time, timesEpochSec: data.hourly.time });
  const temperature = data.hourly.temperature_2m[hourIdx];
  const feelsLike = data.hourly.apparent_temperature[hourIdx];
  const humidity = data.hourly.relative_humidity_2m[hourIdx];
  const windSpeed = data.hourly.wind_speed_10m[hourIdx];
  const windGusts = data.hourly.wind_gusts_10m[hourIdx];
  const windDirection = data.hourly.wind_direction_10m[hourIdx];
  const weatherCode = data.hourly.weather_code[hourIdx];

  if (
    temperature === null ||
    temperature === undefined ||
    feelsLike === null ||
    feelsLike === undefined ||
    humidity === null ||
    humidity === undefined ||
    windSpeed === null ||
    windSpeed === undefined ||
    windGusts === null ||
    windGusts === undefined ||
    windDirection === null ||
    windDirection === undefined ||
    weatherCode === null ||
    weatherCode === undefined
  ) {
    return undefined;
  }

  return {
    time: waypoint.time,
    lat: waypoint.lat,
    lng: waypoint.lng,
    temperature,
    feelsLike,
    humidity,
    windSpeed,
    windGusts,
    windDirection,
    weatherCode,
  };
};

const snapshotsFromClusters = ({
  clusters,
  clusterResults,
  waypoints,
}: {
  clusters: WaypointCluster[];
  clusterResults: Array<OpenMeteoResponse | undefined>;
  waypoints: Waypoint[];
}): WeatherSnapshot[] => {
  const snapshots: WeatherSnapshot[] = [];

  for (let ci = 0; ci < clusters.length; ci++) {
    const cluster = clusters[ci];
    const data = clusterResults[ci];
    if (!cluster || !data) continue;

    for (const wpIdx of cluster.waypointIndices) {
      const wp = waypoints[wpIdx];
      if (!wp) continue;
      const snapshot = snapshotAt({ waypoint: wp, data });
      if (snapshot) snapshots.push(snapshot);
    }
  }
  return snapshots;
};

export const fetchSessionWeather = async ({
  sessionId,
  sessionDateMs,
  durationSec,
  records,
}: {
  sessionId: string;
  sessionDateMs: number;
  durationSec: number;
  records: SessionRecord[];
}): Promise<SessionWeather | undefined> => {
  const waypoints = computeHourlyWaypoints({ sessionDateMs, durationSec, records });
  if (waypoints.length === 0) return undefined;

  const clusters = deduplicateWaypoints({ waypoints, thresholdKm: 10 });
  if (clusters.length === 0) return undefined;

  const sessionEndMs = sessionDateMs + durationSec * MS_PER_SECOND;
  const startDate = toUtcDateStr(sessionDateMs);
  const endDate = toUtcDateStr(sessionEndMs);

  const clusterResults = await Promise.all(
    clusters.map((c) => fetchClusterWeather({ lat: c.lat, lng: c.lng, startDate, endDate })),
  );

  const snapshots = snapshotsFromClusters({ clusters, clusterResults, waypoints });

  if (snapshots.length === 0) return undefined;

  snapshots.sort((a, b) => a.time - b.time);

  return {
    sessionId,
    snapshots,
    fetchedAt: Date.now(),
  };
};

export const toWindSamples = (weather: SessionWeather): WindSample[] =>
  weather.snapshots.map((s) => ({ timeMs: s.time, directionDegrees: s.windDirection }));

export interface ValueRange {
  min: number;
  max: number;
}

interface ConditionChange {
  weatherCode: number;
  time: number;
}

export interface WeatherSummary {
  weatherCode: number;
  temperature: ValueRange;
  feelsLike: ValueRange;
  windSpeed: ValueRange;
  firstChange: ConditionChange | undefined;
}

const rangeOf = (values: number[]): ValueRange => ({
  min: Math.min(...values),
  max: Math.max(...values),
});

export const summarizeWeather = (snapshots: WeatherSnapshot[]): WeatherSummary | undefined => {
  const first = snapshots[0];
  if (first === undefined) return undefined;

  const firstCondition = wmoToCondition(first.weatherCode);
  let firstChange: ConditionChange | undefined;
  const changed = snapshots.find(
    (snapshot) => wmoToCondition(snapshot.weatherCode) !== firstCondition,
  );
  if (changed !== undefined) {
    firstChange = { weatherCode: changed.weatherCode, time: changed.time };
  }

  return {
    weatherCode: first.weatherCode,
    temperature: rangeOf(snapshots.map((snapshot) => snapshot.temperature)),
    feelsLike: rangeOf(snapshots.map((snapshot) => snapshot.feelsLike)),
    windSpeed: rangeOf(snapshots.map((snapshot) => snapshot.windSpeed)),
    firstChange,
  };
};
